import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {ImageSlider} from 'UI/components/modules/ImageSlider';
import type {SliderImage} from 'UI/components/modules/ImageSlider/ImageSlider';
import {ResponsiveImage} from 'UI/components/modules/ResponsiveImage';
import type {ImageSource} from 'UI/components/modules/ResponsiveImage/ResponsiveImage';

import {useImageZoomModal} from './useImageZoomModal';
import {CloseIcon} from 'Images/icons';

/**
 * The props for the `ImageZoomModal` component.
 * Configures a fullscreen modal overlay for zooming into hero images or sliders.
 */
interface ComponentProps {
    /** Alt text for a single image. */
    imageAlt?: string;
    /** Focal point X coordinate (0–100) for single image mode. */
    imageFocalX?: number | null;
    /** Focal point Y coordinate (0–100) for single image mode. */
    imageFocalY?: number | null;
    /** Array of slider images. When length > 1, renders a slider. */
    images?: SliderImage[];
    /** Responsive srcSet for single image mode. */
    imageSrcSet?: ImageSource[];
    /** Single image URL (used when no slider images provided). */
    imageUrl?: string;
    /** Initial slide index to show when opening the modal. */
    initialSlide?: number;
    /** Whether the modal is open. */
    isOpen: boolean;
    /** Callback to close the modal. */
    onClose(): void;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'ImageZoomModal'
     */
    testId?: string;
}

/**
 * A fullscreen modal overlay that displays a hero image or slider at full size
 * for zooming and detailed viewing. Includes a close button and backdrop click to dismiss.
 * Animates in/out with fade and scale transitions.
 *
 * @param props              The component props.
 * @param props.imageAlt     Alt text for the image.
 * @param props.imageFocalX  Focal point X for single image.
 * @param props.imageFocalY  Focal point Y for single image.
 * @param props.imageSrcSet  Responsive srcSet for single image.
 * @param props.imageUrl     URL for the single image.
 * @param props.images       Array of slider images.
 * @param props.initialSlide Initial slide index to show when opening the modal.
 * @param props.isOpen       Whether the modal is visible.
 * @param props.onClose      Close handler.
 * @param props.testId       An optional test ID used for automation selectors. Defaults to `'ImageZoomModal'`.
 * @returns A JSX element rendering the ImageZoomModal, or null when closed.
 *
 * @example
 * ```tsx
 * <ImageZoomModal
 *     images={heroImages}
 *     isOpen={isModalOpen}
 *     onClose={() => setIsModalOpen(false)}
 * />
 * ```
 */
const ImageZoomModal = ({
    imageAlt = '',
    imageFocalX,
    imageFocalY,
    images,
    imageSrcSet,
    imageUrl,
    initialSlide = 0,
    isOpen,
    onClose,
    testId = 'ImageZoomModal'
}: ComponentProps) => {
    const {
        handleBackdropClick, hasSlider, singleImage, singleUrl
    } = useImageZoomModal(isOpen, onClose, images, imageUrl);

    return (
        <AnimatePresence>
            {isOpen && (
                <Backdrop
                    key="image-zoom-backdrop"
                    animate={{opacity: 1}}
                    data-cy={testId}
                    exit={{opacity: 0}}
                    initial={{opacity: 0}}
                    transition={{duration: 0.2}}
                    onClick={handleBackdropClick}
                >
                    <CloseButton
                        aria-label="Schließen"
                        data-cy={`${testId}-close`}
                        type="button"
                        onClick={onClose}
                    >
                        <CloseIcon height={24} width={24} />
                    </CloseButton>
                    <ContentWrapper
                        animate={{
                            opacity: 1,
                            scale: 1
                        }}
                        exit={{
                            opacity: 0,
                            scale: 0.9
                        }}
                        initial={{
                            opacity: 0,
                            scale: 0.9
                        }}
                        transition={{duration: 0.2}}
                    >
                        {hasSlider && images
                            ? (
                                <SliderContainer>
                                    <ImageSlider
                                        height="100%"
                                        images={images}
                                        initialSlide={initialSlide}
                                        objectFit="contain"
                                    />
                                </SliderContainer>
                            )
                            : singleUrl && (
                                <ImageContainer>
                                    <ResponsiveImage
                                        alt={singleImage?.alt ?? imageAlt}
                                        focalX={singleImage?.focalX ?? imageFocalX}
                                        focalY={singleImage?.focalY ?? imageFocalY}
                                        objectFit="contain"
                                        sizes="100vw"
                                        src={singleUrl}
                                        srcSet={singleImage?.srcSet ?? imageSrcSet}
                                        testId={`${testId}-image`}
                                    />
                                </ImageContainer>
                            )}
                    </ContentWrapper>
                </Backdrop>
            )}
        </AnimatePresence>
    );
};

ImageZoomModal.displayName = 'ImageZoomModal';

export {ImageZoomModal};

// ── Styled ──────────────────────────────────────────────────────────────

const Backdrop = styled(motion.div)`
    align-items: center;
    background: rgba(0, 0, 0, 0.92);
    display: flex;
    inset: 0;
    justify-content: center;
    position: fixed;
    z-index: 9999;
`;

const CloseButton = styled.button`
    align-items: center;
    backdrop-filter: blur(8px);
    background: rgba(255, 255, 255, 0.15);
    border: none;
    border-radius: 50%;
    color: ${({theme}) => theme.colors.white};
    cursor: pointer;
    display: flex;
    height: 4.4rem;
    justify-content: center;
    position: absolute;
    right: ${spacing(4)};
    top: ${spacing(4)};
    width: 4.4rem;
    z-index: 10;

    &:active {
        background: rgba(255, 255, 255, 0.25);
    }
`;

const ContentWrapper = styled(motion.div)`
    height: 100%;
    max-height: 80vh;
    max-width: 100vw;
    padding: ${spacing(12)} ${spacing(4)};
    width: 100%;
`;

const SliderContainer = styled.div`
    height: 100%;
    width: 100%;
`;

const ImageContainer = styled.div`
    height: 100%;
    width: 100%;
`;